import { ISendingDomain } from "../models/SendingDomain";
import { IWebsite } from "../models/Website";

export const DEFAULT_FROM_LOCAL_PART = "noreply";

/**
 * Mirrors the Widget API's SenderRules.Resolve so the dashboard can show the From address an
 * email for this website will actually carry. Candidates, in order: the website's attached
 * sending domain, then the customer's SMTP from address. The first one on a verified domain
 * wins; otherwise mail goes from the Wilma platform address with the first candidate as Reply-To.
 */
export type SenderStatus =
  | "verified"        // custom From on a verified domain
  | "pending"         // website has a domain attached but it isn't verified yet
  | "orphaned"        // website references a domain no longer registered on the account
  | "platform";       // nothing attached (or nothing verified) - platform default

export interface WebsiteSender {
  status: SenderStatus;
  /** Address emails will actually come from. Null = Wilma platform address. */
  from: string | null;
  fromName: string | null;
  /** Set when falling back to the platform address so replies still reach the customer. */
  replyTo: string | null;
  /** The address the website is configured to use, even if it can't be used yet. */
  configuredFrom: string | null;
  /** The sending domain record the website points at, when it exists. */
  domain: ISendingDomain | null;
}

export function composeAddress(domain: string | null | undefined, localPart: string | null | undefined) {
  if (!domain || !domain.trim()) return null;
  const local = (localPart?.trim() || DEFAULT_FROM_LOCAL_PART).toLowerCase();
  return `${local}@${domain.trim().toLowerCase()}`;
}

export function domainOf(address: string | null | undefined) {
  if (!address) return null;
  const at = address.lastIndexOf("@");
  if (at < 0 || at === address.length - 1) return null;
  return address.slice(at + 1).trim().toLowerCase();
}

export function resolveWebsiteSender(
  website: IWebsite,
  sendingDomains: ISendingDomain[],
  customer?: { smtpFrom?: string | null; smtpFromName?: string | null; name?: string | null } | null
): WebsiteSender {
  const byDomain = new Map(sendingDomains.map((d) => [d.domain.toLowerCase(), d]));
  const isVerified = (domain: string | null) => !!domain && !!byDomain.get(domain)?.verified;

  const websiteDomain = website.sendingDomain?.trim().toLowerCase() || null;
  const domainRecord = websiteDomain ? byDomain.get(websiteDomain) ?? null : null;
  const configuredFrom = composeAddress(websiteDomain, website.sendingFromLocalPart);
  const configuredName =
    website.sendingFromName?.trim() || customer?.smtpFromName?.trim() || customer?.name?.trim() || null;

  const customerFrom = customer?.smtpFrom?.trim() || null;
  const customerName = customer?.smtpFromName?.trim() || customer?.name?.trim() || null;

  if (configuredFrom && isVerified(websiteDomain)) {
    return {
      status: "verified",
      from: configuredFrom,
      fromName: configuredName,
      replyTo: null,
      configuredFrom,
      domain: domainRecord,
    };
  }

  if (customerFrom && isVerified(domainOf(customerFrom))) {
    return {
      status: "verified",
      from: customerFrom,
      fromName: customerName,
      replyTo: null,
      configuredFrom,
      domain: domainRecord ?? byDomain.get(domainOf(customerFrom)!) ?? null,
    };
  }

  const replyTo = configuredFrom ?? customerFrom;

  return {
    status: websiteDomain ? (domainRecord ? "pending" : "orphaned") : "platform",
    from: null,
    fromName: null,
    replyTo,
    configuredFrom,
    domain: domainRecord,
  };
}
